import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core';
import {
  CmsExtendedFormValue,
  CmsInputDropdownConfig,
  isMobileViewport,
  updateUrlParams,
} from '../../../global/_utils';
import { FormValue, Logger } from '../../../global/_types';
import {
  Calendar,
  CalendarEntry,
  calendarEntryToDate, dateToCalendarEntry, dateToISOString,
  getMonthNames,
  getWeekdayNames,
} from '../../../global/controller/_calendar';

@Component({
  tag: 'cms-date-picker',
  styleUrl: 'cms-date-picker.scss',
  shadow: true
})

export class CmsDatePicker {
  @Element() host: HTMLDivElement;

  @Prop() inputConfig: string | CmsInputDropdownConfig;
  @Prop() config: string | CmsDatePickerConfig;
  @Prop() debug: boolean = false;

  @State() date = Calendar.getToday();
  @State() daysInMonth: number[];
  @State() selectedDate: CalendarEntry;
  @State() eventDates = [];
  @State() isMobile: boolean = isMobileViewport();

  @State() value: string = '';

  private logger: Logger;

  private readonly _whiteLayout = 'white';
  private readonly today: CalendarEntry = Calendar.getToday();

  private inputOptions: CmsInputDropdownConfig;
  private options: CmsDatePickerConfig;

  private minDate: Date = null;
  private maxDate: Date = null;

  private fillStartCount: number;
  private fillEndCount: number;

  private dayNames: string[] = [];
  private monthNames: string[] = [];

  private name: string;
  private label: string;
  private dropdown: HTMLDivElement;
  private inputElement: HTMLInputElement;
  private keyboardInput: HTMLInputElement;
  private resetButton: HTMLElement;
  private errorNode: HTMLDivElement;
  private focus: boolean = false;

  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsDatePicker');

    // Config
    this.inputOptions = typeof (this.inputConfig) === 'string' ?
      JSON.parse(this.inputConfig) as CmsInputDropdownConfig :
      this.inputConfig;

    this.options = typeof (this.config) === 'string' ?
      JSON.parse(this.config) as CmsDatePickerConfig :
      this.config;

    if (this.inputOptions.form) {
      this.name = `${this.inputOptions.form}[${this.inputOptions.name}]`;
    } else {
      this.name = this.inputOptions.name;
    }

    this.init();
  }

  componentDidLoad() {
    this.logger.trace('Component CmsDatePicker loaded');

    // set initial value and call change value handler implementation
    this.valueChanged.emit({ name: this.name, value: this.value, label: this.label});
  }

  private init() {
    this.value = this.inputOptions.initialValue ? this.inputOptions.initialValue : null;
    this.minDate = this.inputOptions.minValue ? new Date(Number(this.inputOptions.minValue)) : null;
    this.maxDate = this.inputOptions.maxValue ? new Date(Number(this.inputOptions.maxValue)) : null;

    // Prefilter by url params
    if (this.inputOptions.setUrlParams) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlValue = urlParams.get(this.name);
      if (urlValue && urlValue !== '' && urlValue !== 'null' && urlValue !== 'undefined' && urlValue !== 'NaN') {
        this.value = urlValue;
      }
    }

    const date = this.value ? new Date(Number(this.value)) : null;
    if (date) {
      this.label = date.toLocaleDateString();
      this.selectedDate = dateToCalendarEntry(date);
    } else {
      this.label = this.value
        ? this.value
        : this.inputOptions.placeholder;
    }

    if (this.dayNames.length == 0) {
      this.dayNames = getWeekdayNames(this.options.locale);
    }
    if (this.monthNames.length == 0) {
      this.monthNames = getMonthNames(this.options.locale);
    }

    this.setCalendarDetails();
  }

  private setCalendarDetails(): void {
    const date = this.getValidDate();
    const calendar = new Calendar(date.year, date.month);
    this.daysInMonth = calendar.getCalendarDays();

    this.fillStartCount = calendar.getFillStartCount();
    this.fillEndCount = (calendar.daysInCalendar - calendar.getFillEndCount());
  }

  private getValidDate(): CalendarEntry {
    let date = this.date;
    if (!('month' in this.date && 'year' in this.date)) {
      date = this.today;
    }

    return date;
  }

  private dayChangedHandler(calendarEntry: CalendarEntry, closeDropdown: boolean = true): void {
    const date = calendarEntryToDate(calendarEntry);
    const millis = '' + date.getTime();
    const dateString = date.toLocaleDateString();

    this.inputElement.value = millis;
    this.value = millis;
    this.label = dateString;

    this.valueChanged.emit({ name: this.name, value: this.value, label: this.label });

    if (closeDropdown) {
      this.toggleDropdown();
    }
    this.hideError();
  }

  private daySelectedHandler = (day: number, closeDropdown: boolean = true): void => {
    this.selectedDate = {
      day,
      month: this.date.month,
      year: this.date.year
    };
    this.dayChangedHandler(this.selectedDate, closeDropdown);
  }

  private getPreviousMonth = (date: CalendarEntry): CalendarEntry => {
    let newDate = {} as CalendarEntry;

    if (date.month !== 1) {
      newDate.month = date.month - 1;
      newDate.year = date.year;
    } else {
      newDate.month = 12;
      newDate.year = date.year - 1;
    }

    return newDate;
  }

  private switchToPreviousMonth = (): void => {
    this.date = this.getPreviousMonth(this.date);

    if (typeof this.date !== 'undefined') {
      delete this.date.day;
    }

    this.setCalendarDetails();
  }

  private switchToNextMonth = (): void => {
    if (this.date.month !== 12) {
      this.date.month += 1;
    } else {
      this.date.month = 1;
      this.date.year += 1;
    }

    delete this.date.day;

    this.setCalendarDetails();
  }

  private switchToCurrentMonth = (): void => {
    this.date.month = this.today.month;
    this.date.year = this.today.year;

    this.setCalendarDetails();
  }

  private switchToToday = (): void => {
    this.switchToCurrentMonth();
    this.daySelectedHandler(this.today.day, false);
  }

  private getDigitClassNames = (day: number, month: number, year: number, index: number): string => {
    let classNameDigit = [];
    if (day.toString().length === 1) {
      classNameDigit.push('padding-single-digit');
    }

    if (this.isToday(day, month, year, index)) {
      classNameDigit.push('active');
    }

    if (this.isSelectedDay(day, index)) {
      classNameDigit.push('selected');
    }

    if (this.eventDates.includes(day)) {
      classNameDigit.push('has-event');
    }

    return classNameDigit.join(' ');
  }

  private isToday(day: number, month: number, year: number, index: number): boolean {
    return this.today.day === day
      && this.today.month === month
      && this.today.year === year
      && !(index < this.fillStartCount || index >= this.fillEndCount);
  }

  private isSelectedDay(day: number, index: number) {
    return typeof this.selectedDate !== 'undefined'
      && this.selectedDate.day === day
      && this.selectedDate.month === this.date.month
      && this.selectedDate.year === this.date.year
      && !(index < this.fillStartCount || index >= this.fillEndCount);
  }

  private hasNextMonth(date: CalendarEntry): boolean {
    if (!this.maxDate) {
      return true;
    }

    const max = dateToCalendarEntry(this.maxDate);
    return (date.month < max.month && date.year == max.year) || date.year < max.year;
  }

  private hasPrevMonth(date: CalendarEntry): boolean {
    if (!this.minDate) {
      return true;
    }

    const prevMonth = this.getPreviousMonth(date);
    const min = dateToCalendarEntry(this.minDate);

    return (prevMonth.month >= min.month && prevMonth.year == min.year) || prevMonth.year > min.year;
  }

  private isDayInRange(numberOfDay: number): boolean {
    if (!this.minDate && !this.maxDate) {
      return true;
    }

    const day = {
      day: numberOfDay,
      month: this.date.month,
      year: this.date.year
    } as CalendarEntry;
    const date = calendarEntryToDate(day);

    if (!date) {
      return false;
    }

    return (!this.minDate || (this.minDate && date.getTime() >= this.minDate.getTime()))
      && (!this.maxDate || (this.maxDate && date.getTime() <= this.maxDate.getTime()));
  }

  hideError() {
    this.errorNode.classList.remove('visible');
  }

  showError() {
    this.errorNode.classList.add('visible');
  }

  @Listen('resetForm', { target: 'document' })
  private resetDate() {
    if (typeof this.selectedDate !== 'undefined') {
      delete this.selectedDate.day;
      delete this.selectedDate.month;
      delete this.selectedDate.year;
    }
    this.date = this.today;
    this.handleReset();
    this.valueChanged.emit({ name: this.name, value: this.value, label: this.label });
    this.inputFocused.emit(this);
  }

  getMinMaxDate(value, fallback): Date {
    if (!value || value == 'undefined') {
      if (fallback) {
        return new Date(Number(fallback));
      }

      return null;
    }

    if (value == 'today') {
      return calendarEntryToDate(this.today);
    }

    return new Date(Number(value));
  }

  private focusInput() {
    if (this.focus) return;

    // set initial value
    if (this.value && this.selectedDate) {
      const date = calendarEntryToDate(this.selectedDate);
      this.keyboardInput.value = dateToISOString(date);
    }
    // focus hidden input
    this.focus = true;
    this.keyboardInput.classList.add('focus');
    this.dropdown.classList.add('focus');
    window.setTimeout(() => {
      this.keyboardInput.focus();
    }, 200);
  }

  private blurInput() {
    this.keyboardInput.classList.remove('focus');
    this.dropdown.classList.remove('focus');
    if (this.resetButton) {
      this.resetButton.focus();
    }
    this.hideDropdown();
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    // exit keyboard insert with tab focus lost or esc
    if (event.key === 'Escape' || (this.focus && event.key === 'Tab')) {
      this.blurInput();
    }

    // set new value
    if (this.focus && event.key === 'Enter') {
      // set value
      const date = new Date(this.keyboardInput.value);
      if (!isNaN(date.valueOf())) {
        const calendar = dateToCalendarEntry(date);
        this.date.month = calendar.month;
        this.date.year = calendar.year;
        this.daySelectedHandler(calendar.day);
      } else {
        this.keyboardInput.value = null;
        this.resetDate();
      }
    }
  }

  @Listen('inputFocused', { target: 'document' })
  hideDropdown() {
    if(!this.dropdown) return;
    this.dropdown.classList.remove('visible');
    this.focus = false;
    if (this.keyboardInput.classList.contains('focus')) {
      this.blurInput();
    }
  }

  @Listen('valueChanged', { target: 'document' })
  handleValueChanged(event: CustomEvent<FormValue>) {
    const element = event.detail as FormValue;

    if (this.name == element.name || !this.inputOptions || !this.inputOptions.dependency ||
      this.inputOptions.dependency.length < 1 || !this.inputOptions.dependency.includes(element.name)) {
      return;
    }

    if (element.name.endsWith('_from')) {
      this.minDate = this.getMinMaxDate(element.value, this.inputOptions.minValue);
      const min = this.minDate ? dateToCalendarEntry(this.minDate) : this.today;
      this.date.month = min.month;
      this.date.year = min.year;
      this.setCalendarDetails();
    } else if (element.name.endsWith('_to')) {
      this.maxDate = this.getMinMaxDate(element.value, this.inputOptions.maxValue);
    }
  }

  @Listen('dropdownOpened', { target: 'document' })
  dropdownOpenedHandler(event: CustomEvent) {
    if (event.detail.name == this.name) {
      this.dropdown.classList.add('visible');
      this.focus = true;
    } else {
      this.hideDropdown();
    }
  }

  handleReset() {
    // Reset url param
    if (this.inputOptions.setUrlParams) {
      updateUrlParams(this.name);
    }
    // Reset data
    this.init();
  }

  @Method()
  validate(): Promise<boolean> {
    if (!this.inputOptions.required) {
      return Promise.resolve(true);
    }

    this.hideError();
    if (this.inputOptions.required && (!this.value || !this.value.trim())) {
      this.showError();
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  toggleDropdown() {
    if (this.dropdown.classList.contains('visible')) {
      this.hideDropdown();
    } else {
      if (this.selectedDate && 'month' in this.selectedDate && 'year' in this.selectedDate) {
        this.date.month = this.selectedDate.month;
        this.date.year = this.selectedDate.year;
        this.setCalendarDetails();
      }

      this.dropdownOpened.emit({ name: this.name });
      this.focus = true;
    }
  }

  render() {
    const date = this.getValidDate();
    // ${this.isPastMonth(date) ? 'disabled' : ''}
    const labelStyles = this.inputOptions.color === this._whiteLayout
      ? { color: '#333333'}
      : {};

    return (
      <Host id={this.name}>
        <div class={`container ${this.inputOptions.color ? this.inputOptions.color : ''}`}>
          <cms-input-label
            config={JSON.stringify({
              form: this.inputOptions.form,
              target: this.name,
              text: this.inputOptions.label,
              required: this.inputOptions.required,
              styles: labelStyles,
              invisible: false
            })}
          />
          <input
            name={this.name}
            type="hidden"
            value=""
            ref={(element: HTMLInputElement) => (this.inputElement = element)}
          />
          <input class={`date-input`}
            name={`${this.name}-keyboard`}
            type="date"
            value=""
            ref={(element: HTMLInputElement) => (this.keyboardInput = element)}
          />
          <div class='dropdown'
               ref={(divElement: HTMLDivElement) => (this.dropdown= divElement)}
               tabIndex={0}
               onFocus={this.focusInput.bind(this)}
          >
            <button class={`date-picker-input`} type="button" onClick={this.toggleDropdown.bind(this)}>
              <span>{this.label}</span><span class="icon" />
            </button>
            {this.value ?
              <button class={`reset-date`}
                      onClick={() => this.resetDate()}
                      ref={(element: HTMLElement) => (this.resetButton = element)}
              ><span class={`reset-icon`} /></button>
              : '' }
            <div class={`calendar-container`}>
              <div class="calendar date-picker">
                <header>
                  <button onClick={this.switchToPreviousMonth}
                          aria-label={this.options.labels?.prev ? this.options.labels?.prev : 'previous month'}
                          disabled={!this.hasPrevMonth(date)}
                          class={`btn-prev button`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path
                      d="M15.293 3.293 6.586 12l8.707 8.707 1.414-1.414L9.414 12l7.293-7.293-1.414-1.414z"/></svg>
                  </button>
                  <span>{this.monthNames[date.month - 1]} {date.year}</span>
                  <button onClick={this.switchToNextMonth}
                          aria-label={this.options.labels?.next ? this.options.labels?.next : 'next month'}
                          disabled={!this.hasNextMonth(date)}
                          class="btn-next button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path
                      d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z" /></svg>
                  </button>
                </header>
                <div class="day-names">
                  {this.dayNames.map(dayName => <span>{dayName}</span>)}
                </div>
                <div class="days-in-month">
                  {this.daysInMonth.map((day, index) => {
                    const classNameDigit = this.getDigitClassNames(day, date.month, date.year, index);
                    if (index < this.fillStartCount || index >= this.fillEndCount) {
                      return (
                        <span class="fillMonth">{this.options.showFillDays ? day : ''}</span>
                      );
                    } else if (!this.isDayInRange(day)) {
                      return (
                        <span class="disabled">{day}</span>
                      );
                    } else {
                      return (
                        <span onClick={() => this.daySelectedHandler(day)}>
                        <i class={classNameDigit}>
                          {day}
                        </i>
                      </span>
                      );
                    }
                  })}
                </div>
                <footer>
                  <button class="btn-today button" onClick={() => this.switchToToday()}>
                    {this.options.labels?.today ? this.options.labels?.today : 'today'}
                  </button>
                </footer>
              </div>
            </div>
          </div>
          <div class="error"
               innerHTML={this.inputOptions.errorMessage}
               ref={(divElement: HTMLDivElement) => (this.errorNode= divElement)}
          />
        </div>
      </Host>
    );
  }

  // Events
  @Event({
    eventName: 'dropdownOpened',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  dropdownOpened: EventEmitter;

  @Event({
    eventName: 'valueChanged',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  valueChanged: EventEmitter<CmsExtendedFormValue>;

  @Event({
    eventName: 'inputFocused',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) inputFocused: EventEmitter;
}

export interface CmsDatePickerConfig {
  locale: string,
  showFillDays: boolean,
  labels: {
    today: string,
    prev: string,
    next: string
  }
}
